import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import AdminRouteGuard from '@/utils/route-guard/AdminRouteGuard';
import AuthGuard from '@/utils/route-guard/AuthGuard';
import SupervisorGuard from '@/utils/route-guard/SupervisorGuard';
import StaffGuard from '@/utils/route-guard/StaffGuard';
import MainLayout from '@/layout/MainLayout';
import Loadable from '@/ui-component/Loadable';
import { useAppSelector } from '@/hooks';
import { getUserHomePath } from '@/utils/role';

// page routing
const DesignSystemPage = Loadable(lazy(() => import('views/design-system')));
const PatientListingPage = Loadable(lazy(() => import('views/pages/patients/listing')));
const MyPatientsListingPage = Loadable(lazy(() => import('views/pages/my-patients/listing')));
const PatientSelectionPage = Loadable(lazy(() => import('views/pages/my-patients/patient-selection')));
const MeetingViewPage = Loadable(lazy(() => import('views/pages/meeting-view')));
const MeetingTranscriptionPage = Loadable(lazy(() => import('views/pages/meeting-transcription')));
const MeetingTranscriptionUploadPage = Loadable(lazy(() => import('views/pages/meeting-transcription/upload')));
const MatchVerificationPage = Loadable(lazy(() => import('views/pages/meeting-transcription/verify')));
const PatientWeekendViewPage = Loadable(lazy(() => import('views/pages/patients/weekend-view')));
const PatientDetailsPage = Loadable(lazy(() => import('views/pages/patients/details')));
const CensusPage = Loadable(lazy(() => import('views/pages/census')));
const DartReportPage = Loadable(lazy(() => import('views/pages/dart-report')));
const InsightsPage = Loadable(lazy(() => import('@/views/pages/insights')));
const CareNavigatorDashboardPage = Loadable(lazy(() => import('@/views/pages/handoff')));
const SupervisorPerformanceScorecardPage = Loadable(lazy(() => import('views/pages/supervisor/performance-scorecard')));
const SupervisorScorecardDetailPage = Loadable(lazy(() => import('views/pages/supervisor/performance-scorecard/detail')));
const AdminHospitalListingPage = Loadable(lazy(() => import('views/pages/admin/hospitals')));
const AdminHospitalCreatePage = Loadable(lazy(() => import('views/pages/admin/hospitals/create')));
const AdminAllUsersPage = Loadable(lazy(() => import('views/pages/admin/users')));
const AdminUserAssociationsPage = Loadable(lazy(() => import('views/pages/admin/user-associations')));
const AdminUserCreatePage = Loadable(lazy(() => import('views/pages/admin/users/create')));
const AdminUserDetailsPage = Loadable(lazy(() => import('views/pages/admin/users/details')));
const AdminReportsDashboardsPage = Loadable(lazy(() => import('views/pages/admin/reports-dashboards')));
const AdminKnowledgeBasePage = Loadable(lazy(() => import('views/pages/admin/knowledge-base')));
const AdminKnowledgeEscalationPhenotypesPage = Loadable(lazy(() => import('views/pages/admin/knowledge-base/escalation-phenotypes')));
const AdminActivityLogsPage = Loadable(lazy(() => import('views/pages/admin/activity-logs')));
const AdminStaticSubscribersPage = Loadable(lazy(() => import('@/views/pages/admin/hospitals/static-subscribers')));
const AdminHospitalDetailsPage = Loadable(lazy(() => import('views/pages/admin/hospitals/details')));
const AdminHospitalManageMappingPage = Loadable(lazy(() => import('views/pages/admin/hospitals/mapping')));
const AdminHospitalManageStaffPage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff')));
const AdminHospitalManageStaffCreatePage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff/create')));
const AdminHospitalManageStaffDetailsPage = Loadable(lazy(() => import('views/pages/admin/hospitals/staff/details')));
const AdminHospitalPopulationScopePage = Loadable(lazy(() => import('views/pages/admin/hospitals/population-scope')));
const AdminHospitalPatientOverridePage = Loadable(lazy(() => import('views/pages/admin/hospitals/population-scope/patient-override')));
const AdminHospitalDataDictionaryPage = Loadable(lazy(() => import('views/pages/admin/hospitals/data-dictionary')));
const AdminHospitalConfigPage = Loadable(lazy(() => import('views/pages/admin/hospitals/hospital-config')));

const RoleBasedHomePage = () => {
    const { user } = useAppSelector(({ auth }) => auth);
    return <Navigate to={getUserHomePath(user)} replace />;
};

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <AdminRouteGuard>
                <MainLayout />
            </AdminRouteGuard>
        </AuthGuard>
    ),
    children: [
        {
            path: '/',
            element: <RoleBasedHomePage />
        },
        {
            path: '/dashboard',
            element: <CareNavigatorDashboardPage />
        },
        {
            path: '/patients',
            element: <PatientListingPage />
        },
        {
            path: '/my-patients',
            element: <MyPatientsListingPage />
        },
        {
            path: '/my-patients/patient-selection',
            element: (
                <StaffGuard>
                    <PatientSelectionPage />
                </StaffGuard>
            )
        },
        {
            path: '/meeting-view',
            element: (
                <SupervisorGuard>
                    <MeetingViewPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/meeting-transcription',
            element: (
                <SupervisorGuard>
                    <MeetingTranscriptionPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/meeting-transcription/upload',
            element: (
                <SupervisorGuard>
                    <MeetingTranscriptionUploadPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/meeting-transcription/:hospitalId/:sessionId/verify',
            element: (
                <SupervisorGuard>
                    <MatchVerificationPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/supervisor/scorecard',
            element: (
                <SupervisorGuard onlyHospitalOrganization>
                    <SupervisorPerformanceScorecardPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/supervisor/scorecard/:userId/:unitId/:date',
            element: (
                <SupervisorGuard onlyHospitalOrganization>
                    <SupervisorScorecardDetailPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/patients/weekend-view/:patientId/:fin/:roomBed',
            element: <PatientWeekendViewPage />
        },
        {
            path: '/patients/:id',
            element: <PatientDetailsPage />
        },
        {
            path: '/census',
            element: <CensusPage />
        },
        {
            path: '/dart-report',
            element: (
                <SupervisorGuard onlyHospitalOrganization>
                    <DartReportPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/insights',
            element: (
                <SupervisorGuard onlyHospitalOrganization>
                    <InsightsPage />
                </SupervisorGuard>
            )
        },
        {
            path: '/static-subscribers',
            element: (
                <SupervisorGuard>
                    <AdminStaticSubscribersPage />
                </SupervisorGuard>
            )
        },
        // Admin routes
        {
            path: '/admin/hospitals',
            element: <AdminHospitalListingPage />
        },
        {
            path: '/admin/hospitals/create',
            element: <AdminHospitalCreatePage />
        },
        {
            path: '/admin/users',
            element: <AdminAllUsersPage />
        },
        {
            path: '/admin/user-associations',
            element: <AdminUserAssociationsPage />
        },
        {
            path: '/admin/users/create',
            element: <AdminUserCreatePage />
        },
        {
            path: '/admin/users/:id/user-detail',
            element: <AdminUserDetailsPage />
        },
        {
            path: '/admin/reports-dashboards',
            element: <AdminReportsDashboardsPage />
        },
        {
            path: '/admin/meeting-transcription',
            element: <MeetingTranscriptionPage />
        },
        {
            path: '/admin/meeting-transcription/upload',
            element: <MeetingTranscriptionUploadPage />
        },
        {
            path: '/admin/meeting-transcription/:hospitalId/upload',
            element: <MeetingTranscriptionUploadPage />
        },
        {
            path: '/admin/meeting-transcription/:hospitalId/:sessionId/verify',
            element: <MatchVerificationPage />
        },
        {
            path: '/admin/knowledge-base',
            element: <AdminKnowledgeBasePage />
        },
        {
            path: '/admin/knowledge-base/escalation-phenotypes',
            element: <AdminKnowledgeEscalationPhenotypesPage />
        },
        {
            path: '/activity-logs',
            element: <AdminActivityLogsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/hospital-details',
            element: <AdminHospitalDetailsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-mapping',
            element: <AdminHospitalManageMappingPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/dashboard',
            element: <CareNavigatorDashboardPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients',
            element: <PatientListingPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/my-patients',
            element: <MyPatientsListingPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/my-patients/patient-selection',
            element: <PatientSelectionPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients/weekend-view/:patientId/:fin/:roomBed',
            element: <PatientWeekendViewPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-patients/:id',
            element: <PatientDetailsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/census',
            element: <CensusPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/dart-report',
            element: <DartReportPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/insights',
            element: <InsightsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-view',
            element: <MeetingViewPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription',
            element: <MeetingTranscriptionPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription/upload',
            element: <MeetingTranscriptionUploadPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-transcription/:sessionId/verify',
            element: <MatchVerificationPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/meeting-view/patient-details/:id',
            element: <PatientDetailsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/scorecard',
            element: <SupervisorPerformanceScorecardPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/scorecard/:userId/:unitId/:date',
            element: <SupervisorScorecardDetailPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff',
            element: <AdminHospitalManageStaffPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff/create',
            element: <AdminHospitalManageStaffCreatePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/manage-staff/:userId/user-detail',
            element: <AdminHospitalManageStaffDetailsPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/static-subscribers',
            element: <AdminStaticSubscribersPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope',
            element: <AdminHospitalPopulationScopePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope/patient-override/:overrideId',
            element: <AdminHospitalPatientOverridePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/population-scope/patient-override',
            element: <AdminHospitalPatientOverridePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/data-dictionary',
            element: <AdminHospitalDataDictionaryPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/hospital-config',
            element: <AdminHospitalConfigPage />
        },
        {
            path: '/admin/hospitals/:hospitalId/playbooks',
            element: <AdminKnowledgeBasePage />
        },
        {
            path: '/admin/hospitals/:hospitalId/playbooks/escalation-phenotypes',
            element: <AdminKnowledgeEscalationPhenotypesPage />
        },

        ...(import.meta.env.DEV
            ? [
                  {
                      path: '/design-system',
                      element: <DesignSystemPage />
                  },
                  {
                      path: '/design-system/:componentId',
                      element: <DesignSystemPage />
                  }
              ]
            : [])
    ]
};

export default MainRoutes;
